import { useState, useEffect, useCallback } from "react";
import { fetchPaymentHistory } from "../services/api";

const DEFAULT_PAGE_SIZE = 5;

/**
 * Custom hook for payment history operations with optional pagination.
 * Backward compatible: works with APIs that don't support pagination.
 */
export const usePaymentHistory = (userId, pageSize = DEFAULT_PAGE_SIZE) => {
	const [allOrders, setAllOrders] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [page, setPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [totalPages, setTotalPages] = useState(1);
	const [serverPaginated, setServerPaginated] = useState(false);

	const limit = typeof pageSize === "number" && pageSize >= 1 ? pageSize : DEFAULT_PAGE_SIZE;
	// Slice for client-side pagination when API returns all items
	const paymentHistory = serverPaginated
		? allOrders
		: allOrders.slice((page - 1) * limit, page * limit);

	const refetch = useCallback(
		async (pageNum) => {
			if (!userId) {
				setAllOrders([]);
				setTotalCount(0);
				setTotalPages(1);
				setServerPaginated(false);
				return;
			}

			setLoading(true);
			setError(null);

			try {
				const options = { page: pageNum, limit };
				const data = await fetchPaymentHistory(userId, options);

				if (data.success) {
					const orders = data.orders || [];

					// Support both paginated and non-paginated API responses
					if (typeof data.total === "number") {
						setServerPaginated(true);
						setAllOrders(orders);
						setTotalCount(data.total);
						setTotalPages(data.totalPages ?? Math.max(1, Math.ceil(data.total / (data.limit || limit))));
					} else {
						// Old API: all items in one response - use client-side pagination
						setServerPaginated(false);
						setAllOrders(orders);
						setTotalCount(orders.length);
						setTotalPages(Math.max(1, Math.ceil(orders.length / limit)));
						setPage(1);
					}
				} else {
					throw new Error(data.message || "Failed to fetch payment history");
				}
			} catch (err) {
				const errorMessage =
					err?.response?.data?.message ||
					err?.message ||
					"Failed to load payment history";
				setError(errorMessage);
				console.error("Failed to load payment history", err);
			} finally {
				setLoading(false);
			}
		},
		[userId, limit]
	);

	useEffect(() => {
		if (userId) {
			// Server-paginated: refetch on page change. Client-paginated: fetch once (page 1).
			if (serverPaginated) {
				refetch(page);
			} else {
				refetch(1);
			}
		}
	}, [userId, serverPaginated ? page : 1, refetch]);

	const goToPage = useCallback((p) => {
		if (p >= 1) setPage(p);
	}, []);

	return {
		paymentHistory,
		loading,
		error,
		refetch: () => {
			setPage(1);
			refetch(1);
		},
		page,
		totalPages,
		totalCount,
		pageSize: limit,
		hasNext: page < totalPages,
		hasPrev: page > 1,
		goToPage,
		setPage,
	};
};
